import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import CONFIG from "config/config";

// Mirror of oppizi-platform's `Containers/Auth/SignUp/components/Captcha.js`.
// Renders the Google reCAPTCHA v2 widget only in production. In dev/local
// the parent's `isCaptchaSuccess` should be initialized to `true` so the
// signup form unblocks without a real challenge.

const isProduction = (): boolean => process.env.REACT_APP_NODE_ENV === "prod";

// One-time noisy warning when prod is missing the site key, so an ops
// misconfiguration is at least visible in browser logs / Bugsnag breadcrumbs
// instead of silently disabling bot protection.
let warnedMissingKey = false;
const warnIfMissingKey = () => {
  if (warnedMissingKey) return;
  warnedMissingKey = true;
  if (isProduction() && !CONFIG.GOOGLE_CAPTCHA) {
    // eslint-disable-next-line no-console
    console.warn("[Captcha] REACT_APP_GOOGLE_CAPTCHA is not set — reCAPTCHA disabled in prod build.");
  }
};

type Props = {
  onChangeCaptcha: (value: string | null | false) => void;
};

export const Captcha: React.FC<Props> = ({ onChangeCaptcha }) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isExpired, setExpired] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsScriptLoaded(true), 1000);
    return () => clearTimeout(t);
  }, [isExpired]);

  // Bail in non-prod, OR in prod when the site key isn't configured —
  // mirrors `isCaptchaRequired()` below. We never render <ReCAPTCHA> with
  // an empty/undefined sitekey; the widget would just throw at runtime.
  if (!isCaptchaRequired()) {
    warnIfMissingKey();
    return null;
  }

  const onExpired = () => {
    setExpired(true);
    setIsScriptLoaded(false);
    onChangeCaptcha(false);
  };

  return (
    <div>
      {isScriptLoaded && (
        <div style={{ display: "none" }}>
          <ReCAPTCHA sitekey={CONFIG.GOOGLE_CAPTCHA} />
        </div>
      )}
      <ReCAPTCHA sitekey={CONFIG.GOOGLE_CAPTCHA} onChange={onChangeCaptcha} onExpired={onExpired} />
    </div>
  );
};

// Captcha is required iff we're on a prod build AND the site key is
// configured. The second condition matters because `CONFIG.GOOGLE_CAPTCHA`
// has no hardcoded fallback (see config.js): if a prod build ships
// without the env var, we deliberately fail-open (no captcha widget,
// signup still works) instead of fail-closed (signup blocked) — the
// console warning above flags the misconfiguration to ops.
export const isCaptchaRequired = (): boolean => isProduction() && !!CONFIG.GOOGLE_CAPTCHA;

export default Captcha;
